// umlaut-check: skip — Variablen-/Funktionsnamen ("kopfDirty", "ladeDetail"
// usw.) sind technische Bezeichner; sichtbare Texte tragen Umlaute korrekt.

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Card, Col, Container, OverlayTrigger, Row, Tooltip} from "react-bootstrap";
import {router} from "@inertiajs/react";
import axios from "axios";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import FeldMitHilfe from "../../Components/Cosmos/FeldMitHilfe.jsx";
import SelectList from "../../Components/SelectList.jsx";
import KwSelect, {kwLabel, naechsteKw} from "../../Components/Cosmos/KwSelect.jsx";
import DatePickerField from "../../Components/Statistics/DatePickerField.jsx";
import LieferPositionenTabelle from "../../Components/Cosmos/LieferPositionenTabelle.jsx";
import AenderungsZeitleiste from "../../Components/Cosmos/AenderungsZeitleiste.jsx";
import UngespeichertHinweis from "../../Components/Cosmos/UngespeichertHinweis.jsx";
import BestaetigenModal from "../../Components/Cosmos/BestaetigenModal.jsx";
import LkwLadung from "../../Components/Cosmos/LkwLadung.jsx";
import {lieferungMarkeFarbe} from "../../Components/Cosmos/ladungsFarben.js";
import {formatFarbnummer, formatMenge, lieferpositionStatusLabel, markeLabel} from "../../Components/Cosmos/format.js";

// Rückfall, solange die Kapazitätseinstellungen noch nicht vom Server
// geladen sind (Entwurf ohne gespeicherte Lieferung) - dieselben
// Startwerte wie App\Services\Cosmos\LieferungenQuery::einstellungZahl().
const EINSTELLUNGEN_RUECKFALL = {lkw_kapazitaet_stueck: 45570, palette_stueck: 744};

const STATUS_OPTIONEN = [
    {value: "geplant", label: "Geplant"},
    {value: "bestaetigt", label: "Bestätigt"},
    {value: "kommissionierung", label: "Kommissionierung"},
    {value: "teilgeliefert", label: "Teilgeliefert"},
    {value: "geliefert", label: "Geliefert"},
    {value: "verschoben", label: "Verschoben"},
];

const MARKE_OPTIONEN = [
    {value: "", label: "– keine –"},
    {value: "Molotow", label: "Molotow"},
    {value: "Burner", label: "Burner"},
    {value: "Flame", label: "Flame"},
    {value: "Cosmos", label: "Cosmos"},
];

const ZIELORT_OPTIONEN = [
    {value: "", label: "– keine –"},
    {value: "SNV Weilheim", label: "SNV Weilheim"},
    {value: "Lahr", label: "Lahr"},
    // Punkt 8 (Meeting 22.09.2026): Cosmos produziert für das eigene
    // Pufferlager statt direkt für SNV Weilheim zu liefern - Kapitalbindung
    // statt Zulauf, siehe App\Services\Cosmos\LieferpositionenPlanung::ZIELORT_PUFFER (F2).
    {value: "Cosmos-Pufferlager", label: "Cosmos-Pufferlager (Produktion, kein SNV-Zulauf)"},
];

const INCOTERM_OPTIONEN = [
    {value: "", label: "– keine –"},
    {value: "DAP", label: "DAP"},
    {value: "EXW", label: "EXW"},
];

const QUELLE_OPTIONEN = [
    {value: "excel", label: "Excel"},
    {value: "mail", label: "E-Mail"},
    {value: "hand", label: "Von Hand"},
    {value: "api", label: "Schnittstelle"},
    {value: "weclapp", label: "Weclapp"},
];

const SICHERHEIT_OPTIONEN = [
    {value: "zugesagt", label: "Zugesagt"},
    {value: "geplant", label: "Geplant"},
    {value: "vermutet", label: "Vermutet"},
    {value: "ist", label: "Ist"},
];

function leereZeileAusServer(p) {
    return {...p, _key: `bestehend-${p.id}`, _dirty: false};
}

/**
 * Kopfdaten eines noch nicht gespeicherten Entwurfs (Sichtprüfung 1,
 * Befund 5/6): KW und KW Wunsch bereits auf die nächste Woche vorbelegt
 * (Montag ab heute), alles andere leer bzw. auf den üblichen Anfangswert.
 * Angelegt wird die Lieferung erst beim ersten "Speichern" (CosmosLieferungen
 * legt dafür nichts mehr sofort in der Datenbank an).
 */
function entwurfKopf() {
    const kw = naechsteKw();

    return {
        id: null,
        kw,
        kw_wunsch: kw,
        kw_zusage: null,
        kw_ist: null,
        lkw_bezeichnung: "",
        cosmos_order_id: "",
        customer_order_reference: "",
        weclapp_bestellnummer: "",
        bestelldatum: null,
        versand_wunsch: null,
        versand_geplant: null,
        versand_ist: null,
        rechnungsdatum: null,
        status: "geplant",
        marke: "",
        incoterm: "",
        zielort: "",
        quelle: "hand",
        sicherheit: "geplant",
        bemerkung: "",
        menge_wunsch: 0,
        menge_zusage: 0,
        menge_ist: 0,
    };
}

function kopfFelder(kopf) {
    return {
        kw: kopf.kw,
        kw_wunsch: kopf.kw_wunsch,
        kw_zusage: kopf.kw_zusage,
        kw_ist: kopf.kw_ist,
        lkw_bezeichnung: kopf.lkw_bezeichnung,
        cosmos_order_id: kopf.cosmos_order_id,
        customer_order_reference: kopf.customer_order_reference,
        weclapp_bestellnummer: kopf.weclapp_bestellnummer,
        bestelldatum: kopf.bestelldatum,
        versand_wunsch: kopf.versand_wunsch,
        versand_geplant: kopf.versand_geplant,
        versand_ist: kopf.versand_ist,
        rechnungsdatum: kopf.rechnungsdatum,
        status: kopf.status,
        marke: kopf.marke,
        incoterm: kopf.incoterm,
        zielort: kopf.zielort,
        quelle: kopf.quelle,
        sicherheit: kopf.sicherheit,
        bemerkung: kopf.bemerkung,
    };
}

/** "1 Position" / "3 Positionen" - Pluralform für das Lösch-Bestätigungs-Modal. */
function positionenText(anzahl) {
    return anzahl === 1 ? "1 Position" : `${anzahl} Positionen`;
}

/**
 * Detailansicht und Erfassung einer Cosmos-Lieferung (AP5, PLAN.md
 * Abschnitt 5/6, kein Menüpunkt - Einstieg über Cosmos/Lieferungen.jsx).
 * Kopf und Positionen werden getrennt gespeichert (PUT Kopf, POST
 * Positionen gesammelt), beides ohne Seiten-Neuladen. Ungespeicherte
 * Änderungen werden beim Verlassen der Seite über einen eigenen
 * Hinweis-Dialog abgefangen (kein confirm()).
 *
 * "id: null" (Route "/cosmos/lieferungen/neu") ist ein ENTWURF (Sichtprüfung
 * 1, Befund 5): Es wird nichts vom Server geladen und nichts angelegt, bis
 * jemand tatsächlich speichert - erst dann entsteht die Lieferung und die
 * Seite wechselt auf ihre echte URL.
 */
export default function CosmosLieferungDetail({id = null}) {
    const istEntwurf = id === null;
    const {addToast} = useExternalControls();
    const [kopf, setKopf] = useState(() => (istEntwurf ? entwurfKopf() : null));
    const [positionen, setPositionen] = useState([]);
    const [entfernteIds, setEntfernteIds] = useState([]);
    const [aenderungen, setAenderungen] = useState([]);
    const [einstellungen, setEinstellungen] = useState(EINSTELLUNGEN_RUECKFALL);
    const [canEdit, setCanEdit] = useState(istEntwurf);
    const [kopfDirty, setKopfDirty] = useState(false);
    const [ladefehler, setLadefehler] = useState(null);
    const [ladefehlerStatus, setLadefehlerStatus] = useState(null);
    const [speichertGerade, setSpeichertGerade] = useState(false);
    const [zeigeHinweis, setZeigeHinweis] = useState(false);
    const [zielUrl, setZielUrl] = useState(null);
    const [loeschenOffen, setLoeschenOffen] = useState(false);
    const [loeschtGerade, setLoeschtGerade] = useState(false);

    const dirtyRef = useRef(false);
    const positionenDirty = positionen.some((p) => p._dirty) || entfernteIds.length > 0;

    // Palettenstellplätze für die interaktive Lastwagen-Darstellung dieser
    // Lieferung (LkwLadung.jsx): effektive Menge je Position ist die
    // tatsächlich verladene (menge_ist), sonst die zugesagte, sonst die
    // gewünschte Menge - eine bewusst einfache Regel für diese Anzeige, NICHT
    // dieselbe wie der Bedarfsrechner (App\Services\Cosmos\LieferpositionenPlanung::effektiveZeilen(),
    // Rest über menge_ist hinaus für die Prognose). Neu angelegte, noch nicht
    // gespeicherte Zeilen haben noch keine "paletten"/"farbwert" vom Server
    // und erscheinen erst nach dem Speichern im Raster.
    const kapazitaetPaletten = einstellungen.palette_stueck > 0 ? Number(einstellungen.lkw_kapazitaet_stueck) / Number(einstellungen.palette_stueck) : null;
    const ladungsEintraege = useMemo(
        () =>
            positionen
                .filter((p) => p.paletten !== undefined && p.paletten !== null)
                .map((p) => ({
                    id: p._key,
                    label: `${p.farbnummer !== null && p.farbnummer !== undefined ? formatFarbnummer(p.farbnummer, p.farbnummer_unter) + " " : ""}${p.farbname || p.bezeichnung || p.artikel_nummer}`,
                    unterlabel: p.artikel_nummer,
                    menge: p.menge_ist || p.menge_zusage || p.menge_wunsch || 0,
                    paletten: p.paletten,
                    farbwert: p.farbwert,
                    gruppe: p.marke || "unbekannt",
                    gruppeLabel: markeLabel(p.marke),
                    farbe: lieferungMarkeFarbe(p.marke),
                    bezug: `Lieferung ${istEntwurf ? "(Entwurf)" : `${kwLabel(kopf?.kw)} – #${kopf?.id}`} · Status ${lieferpositionStatusLabel(p.status)}`,
                })),
        [positionen, kopf?.kw, kopf?.id, istEntwurf]
    );

    useEffect(() => {
        dirtyRef.current = kopfDirty || positionenDirty;
    }, [kopfDirty, positionenDirty]);

    const ladeDetail = useCallback(() => {
        if (istEntwurf) {
            // Ein Entwurf existiert serverseitig noch nicht - hier gibt es
            // nichts zu laden.
            return;
        }
        axios
            .get(`/cosmos/daten/lieferungen/${id}`)
            .then(({data}) => {
                setKopf(data.lieferung);
                setPositionen((data.positionen || []).map(leereZeileAusServer));
                setAenderungen(data.aenderungen || []);
                setCanEdit(!!data.meta?.abilities?.canEdit);
                setEinstellungen(data.einstellungen || EINSTELLUNGEN_RUECKFALL);
                setEntfernteIds([]);
                setKopfDirty(false);
            })
            .catch((e) => {
                setLadefehlerStatus(e.response?.status || null);
                setLadefehler(e.response?.data?.message || "Die Lieferung konnte nicht geladen werden.");
            });
    }, [id, istEntwurf]);

    useEffect(() => {
        ladeDetail();
    }, [ladeDetail]);

    // Verlässt jemand die Seite mit ungespeicherten Änderungen (Klick auf
    // Menü, "Zurück zur Liste" o. Ä.), fängt dieser Listener die
    // Inertia-Navigation ab und zeigt den eigenen Hinweis-Dialog statt
    // stillschweigend Daten zu verlieren (kein confirm()).
    //
    // Sichtprüfung 1, Befund 2: "router.on('before', callback)" liefert dem
    // Callback ein CustomEvent, dessen Nutzlast unter "event.detail.visit"
    // liegt - nicht der Visit selbst (die alte Fassung griff auf "visit.url"
    // direkt zu und bekam dadurch immer "undefined"). Außerdem fängt dieser
    // Listener nur noch eine ECHTE Navigation auf eine ANDERE Seite ab, kein
    // GET, keine Methode ungleich GET und keinen Hintergrundabgleich des
    // Layouts (Journal-Polling ruft alle 30 s "router.reload({only: […]})"
    // auf dieselbe URL mit gesetztem "only"/"preserveState" auf).
    useEffect(() => {
        const entferneListener = router.on("before", (event) => {
            if (!dirtyRef.current) {
                return;
            }

            const visit = event.detail.visit;
            const istHintergrundabgleich =
                visit.method !== "get" ||
                visit.preserveState ||
                (Array.isArray(visit.only) && visit.only.length > 0) ||
                (Array.isArray(visit.except) && visit.except.length > 0) ||
                (Array.isArray(visit.reset) && visit.reset.length > 0);
            if (istHintergrundabgleich) {
                return;
            }

            const zielHref = visit.url instanceof URL ? visit.url.href : String(visit.url);
            if (zielHref === window.location.href) {
                return;
            }

            setZielUrl(zielHref);
            setZeigeHinweis(true);

            return false;
        });
        return () => entferneListener();
    }, []);

    const kopfFeldAendern = (feld, wert) => {
        setKopf((k) => ({...k, [feld]: wert}));
        setKopfDirty(true);
    };

    /**
     * Speichert Kopf und Positionen in einem Schritt. Bei einem Entwurf
     * entsteht die Lieferung dabei erstmals (POST statt PUT); der Aufrufer
     * bekommt die entstandene bzw. bestehende ID zurück, um bei Bedarf dahin
     * zu navigieren - "speichern()" selbst navigiert nicht.
     */
    const speichern = () => {
        setSpeichertGerade(true);

        const kopfSpeichern = istEntwurf
            ? axios.post("/cosmos/daten/lieferungen", kopfFelder(kopf))
            : kopfDirty
              ? axios.put(`/cosmos/daten/lieferungen/${id}`, kopfFelder(kopf))
              : Promise.resolve(null);

        return kopfSpeichern
            .then((antwort) => {
                const neueId = istEntwurf ? antwort.data.lieferung.id : id;

                return axios
                    .post(`/cosmos/daten/lieferungen/${neueId}/positionen`, {
                        positionen: positionen.map((p) => ({
                            id: p.id,
                            artikel_nummer: p.artikel_nummer,
                            cosmos_sku: p.cosmos_sku,
                            bezeichnung: p.bezeichnung,
                            marke: p.marke,
                            menge_wunsch: p.menge_wunsch ?? 0,
                            menge_zusage: p.menge_zusage ?? 0,
                            menge_ist: p.menge_ist ?? 0,
                            menge_vorschlag_neu: p.menge_vorschlag_neu,
                            kw_wunsch: p.kw_wunsch,
                            kw_zusage: p.kw_zusage,
                            kw_ist: p.kw_ist,
                            status: p.status,
                            prioritaet: p.prioritaet,
                            ampel: p.ampel,
                            quelle: p.quelle || "hand",
                            sicherheit: p.sicherheit || "geplant",
                        })),
                        entfernte_ids: entfernteIds,
                    })
                    .then(({data}) => ({data, neueId}));
            })
            .then(({data, neueId}) => {
                setKopf(data.lieferung);
                setPositionen((data.positionen || []).map(leereZeileAusServer));
                setAenderungen(data.aenderungen || []);
                setEinstellungen(data.einstellungen || EINSTELLUNGEN_RUECKFALL);
                setEntfernteIds([]);
                setKopfDirty(false);
                dirtyRef.current = false;
                addToast(<FlexToast variant="success" title="Cosmos-Lieferung" message="Gespeichert." />);

                return {neueId};
            })
            .catch((e) => {
                addToast(<FlexToast variant="danger" title="Cosmos-Lieferung" autohide={false} message={e.response?.data?.message || "Speichern fehlgeschlagen."} />);
                throw e;
            })
            .finally(() => setSpeichertGerade(false));
    };

    /** Speichert und wechselt danach auf die endgültige URL, falls aus einem Entwurf gerade eine echte Lieferung wurde. */
    const speichernUndBleiben = () => {
        speichern().then(({neueId}) => {
            if (istEntwurf) {
                router.visit(`/cosmos/lieferungen/${neueId}`, {replace: true});
            }
        });
    };

    const loeschen = () => {
        setLoeschtGerade(true);
        axios
            .delete(`/cosmos/daten/lieferungen/${id}`)
            .then(() => {
                dirtyRef.current = false;
                setLoeschenOffen(false);
                addToast(<FlexToast variant="success" title="Cosmos-Lieferung" message="Gelöscht." />);
                router.visit("/cosmos/lieferungen");
            })
            .catch((e) => {
                addToast(<FlexToast variant="danger" title="Cosmos-Lieferung" autohide={false} message={e.response?.data?.message || "Löschen fehlgeschlagen."} />);
            })
            .finally(() => setLoeschtGerade(false));
    };

    if (ladefehler) {
        // Sichtprüfung 1, Befund 12: eine gelöschte oder nie existierende
        // Lieferung zeigte bislang eine leere weiße Seite (404 lief im
        // Hintergrund ins Leere) - jetzt eine erklärende Hinweis-Karte mit
        // Rückweg statt eines technischen Fehlertexts, sobald der Server
        // "nicht gefunden" meldet.
        const nichtGefunden = ladefehlerStatus === 404;

        return (
            <Container fluid className="pt-2 pb-4">
                <Card className="border-warning-subtle">
                    <Card.Body className="text-center py-5">
                        <Card.Title as="h1" className="h5">
                            {nichtGefunden ? "Diese Lieferung gibt es nicht (mehr)" : "Die Lieferung konnte nicht geladen werden"}
                        </Card.Title>
                        <Card.Text className="text-muted">
                            {nichtGefunden
                                ? "Sie wurde gelöscht, oder die Adresse ist nicht (mehr) gültig."
                                : ladefehler}
                        </Card.Text>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => router.visit("/cosmos/lieferungen")}>
                            Zurück zur Liste
                        </button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!kopf) {
        return null;
    }

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="lieferung-detail">
                Zeigt die Positionen einer einzelnen Lieferung mit altem und neuem Termin nebeneinander, damit
                jede Verschiebung nachvollziehbar bleibt.
            </PurposeBox>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Kopfdaten, Positionen und Änderungshistorie dieser Lieferung.">
                        {/* N9a (Sichtprüfung 2, 22.09.2026): "Lieferung KW 39/26 – #12" -
                            KW in Klartext ("KW 39/26" statt "2026-39"), Nummer statt
                            der oft leeren LKW-Bezeichnung. */}
                        {istEntwurf ? "Neue Lieferung" : `Lieferung ${kwLabel(kopf.kw)} – #${kopf.id}`}
                    </HeaderWithHelp>
                </h1>
                <div className="d-flex gap-2">
                    <OverlayTrigger placement="top" overlay={<Tooltip id="zurueck-tip">Verlässt diese Seite ohne zu speichern, sofern nichts Ungespeichertes offen ist.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => router.visit("/cosmos/lieferungen")}>
                                Zurück zur Liste
                            </button>
                        </span>
                    </OverlayTrigger>
                    {canEdit && !istEntwurf && (
                        <OverlayTrigger placement="top" overlay={<Tooltip id="loeschen-tip">Löscht diese Lieferung samt aller Positionen unwiderruflich - die bisherige Historie bleibt erhalten.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setLoeschenOffen(true)}>
                                    Löschen
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                    {canEdit && (
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip id="speichern-tip">
                                    {istEntwurf
                                        ? "Legt diese Lieferung mit Kopfdaten und allen bereits erfassten Positionen neu an."
                                        : "Speichert Kopfdaten und alle Positionen (neu, geändert, entfernt) in einem Schritt."}
                                </Tooltip>
                            }
                        >
                            <span>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={speichertGerade || (!istEntwurf && !(kopfDirty || positionenDirty))}
                                    onClick={speichernUndBleiben}
                                >
                                    {speichertGerade ? "speichert…" : "Speichern"}
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                </div>
            </div>

            {kopf.zielort === "Cosmos-Pufferlager" && (
                <div className="alert alert-warning-subtle border border-warning-subtle py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                    <HeaderWithHelp help="Cosmos produziert hier für das Pufferlager statt direkt für SNV Weilheim zu liefern (Meeting 22.09.2026, Punkt 8). Die Menge gilt als gebundenes Kapital, zählt aber nicht als SNV-Zulauf in Lieferfähigkeit, Bestellvorschlägen oder Lieferplan.">
                        <span className="fw-semibold">Pufferlager-Produktion</span>
                    </HeaderWithHelp>
                    {kopf.kapitalbindung && (
                        <span className="text-muted">
                            {formatMenge(kopf.kapitalbindung.menge)} Stück gebunden ({kopf.kapitalbindung.menge_basis}) – {kopf.kapitalbindung.wert_fehlt_grund}
                        </span>
                    )}
                </div>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <Row className="g-3">
                        <Col xs={6} sm={3} md={2}>
                            <FeldMitHilfe id="kw" label="KW (Zuordnung)" help="Kalenderwoche, der diese Lieferung zugeordnet ist.">
                                <KwSelect id="kw" value={kopf.kw} onChange={(v) => kopfFeldAendern("kw", v)} allowEmpty={false} disabled={!canEdit} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3} md={2}>
                            <FeldMitHilfe id="kw_wunsch" label="KW Wunsch" help="Kalenderwoche, in der wir die Lieferung wünschen.">
                                <KwSelect id="kw_wunsch" value={kopf.kw_wunsch} onChange={(v) => kopfFeldAendern("kw_wunsch", v)} allowEmpty={false} disabled={!canEdit} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3} md={2}>
                            <FeldMitHilfe id="kw_zusage" label="KW Zusage" help="Von Cosmos zugesagte Kalenderwoche.">
                                <KwSelect id="kw_zusage" value={kopf.kw_zusage} onChange={(v) => kopfFeldAendern("kw_zusage", v)} disabled={!canEdit} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3} md={2}>
                            <FeldMitHilfe id="kw_ist" label="KW Ist" help="Kalenderwoche des tatsächlichen Versands.">
                                <KwSelect id="kw_ist" value={kopf.kw_ist} onChange={(v) => kopfFeldAendern("kw_ist", v)} disabled={!canEdit} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <FeldMitHilfe id="lkw_bezeichnung" label="LKW-Bezeichnung" help="Freitext wie „Truck 1 CW39“, wie im Cosmos-Vorbild benannt.">
                                <input id="lkw_bezeichnung" type="text" className="form-control form-control-sm" value={kopf.lkw_bezeichnung || ""} disabled={!canEdit} onChange={(e) => kopfFeldAendern("lkw_bezeichnung", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>

                        <Col xs={12} sm={4}>
                            <FeldMitHilfe id="cosmos_order_id" label="Cosmos-Nr." help="Cosmos-Auftragsnummer, z. B. „BE517769“, sobald bekannt.">
                                <input id="cosmos_order_id" type="text" className="form-control form-control-sm" value={kopf.cosmos_order_id || ""} disabled={!canEdit} onChange={(e) => kopfFeldAendern("cosmos_order_id", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={12} sm={4}>
                            <FeldMitHilfe id="customer_order_reference" label="Unsere Referenz" help="Unsere eigene Bestellreferenz für diese Lieferung.">
                                <input id="customer_order_reference" type="text" className="form-control form-control-sm" value={kopf.customer_order_reference || ""} disabled={!canEdit} onChange={(e) => kopfFeldAendern("customer_order_reference", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={12} sm={4}>
                            <FeldMitHilfe id="weclapp_bestellnummer" label="Weclapp-Verweis" help="Nur ein Verweis auf die Weclapp-Bestellung (z. B. „P1090“) – keine Datenquelle (Entscheidung F1).">
                                <input id="weclapp_bestellnummer" type="text" className="form-control form-control-sm" value={kopf.weclapp_bestellnummer || ""} disabled={!canEdit} onChange={(e) => kopfFeldAendern("weclapp_bestellnummer", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>

                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="status" label="Status" help="Aktueller Status der gesamten Lieferung.">
                                <SelectList id="status" value={kopf.status} options={STATUS_OPTIONEN} onChange={(v) => kopfFeldAendern("status", v)} disabled={!canEdit} ariaLabel="Status" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="marke" label="Marke" help="Molotow, Burner, Flame oder Cosmos.">
                                <SelectList id="marke" value={kopf.marke || ""} options={MARKE_OPTIONEN} onChange={(v) => kopfFeldAendern("marke", v)} disabled={!canEdit} ariaLabel="Marke" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="incoterm" label="Incoterm" help="Lieferbedingung.">
                                <SelectList id="incoterm" value={kopf.incoterm || ""} options={INCOTERM_OPTIONEN} onChange={(v) => kopfFeldAendern("incoterm", v)} disabled={!canEdit} ariaLabel="Incoterm" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="zielort" label="Zielort" help="SNV Weilheim für Molotow/Burner, Lahr für Flame, Cosmos-Pufferlager bei einer Produktion für den Puffer statt direkter SNV-Lieferung.">
                                <SelectList id="zielort" value={kopf.zielort || ""} options={ZIELORT_OPTIONEN} onChange={(v) => kopfFeldAendern("zielort", v)} disabled={!canEdit} ariaLabel="Zielort" />
                            </FeldMitHilfe>
                        </Col>

                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="bestelldatum" label="Bestelldatum" help="Wann wir bestellt haben.">
                                <DatePickerField id="bestelldatum" value={kopf.bestelldatum} onChange={(v) => kopfFeldAendern("bestelldatum", v)} disabled={!canEdit} size="sm" label="Bestelldatum" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="versand_wunsch" label="Versand Wunsch" help="Von uns gewünschter Versandtermin.">
                                <DatePickerField id="versand_wunsch" value={kopf.versand_wunsch} onChange={(v) => kopfFeldAendern("versand_wunsch", v)} disabled={!canEdit} size="sm" label="Versand Wunsch" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="versand_geplant" label="Versand Geplant" help="Von Cosmos geplanter Versandtermin.">
                                <DatePickerField id="versand_geplant" value={kopf.versand_geplant} onChange={(v) => kopfFeldAendern("versand_geplant", v)} disabled={!canEdit} size="sm" label="Versand Geplant" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="versand_ist" label="Versand Ist" help="Tatsächlicher Versandtermin.">
                                <DatePickerField id="versand_ist" value={kopf.versand_ist} onChange={(v) => kopfFeldAendern("versand_ist", v)} disabled={!canEdit} size="sm" label="Versand Ist" />
                            </FeldMitHilfe>
                        </Col>

                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="rechnungsdatum" label="Rechnungsdatum" help="Datum der Cosmos-Rechnung.">
                                <DatePickerField id="rechnungsdatum" value={kopf.rechnungsdatum} onChange={(v) => kopfFeldAendern("rechnungsdatum", v)} disabled={!canEdit} size="sm" label="Rechnungsdatum" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="quelle" label="Quelle" help="Woher diese Kopfdaten stammen.">
                                <SelectList id="quelle" value={kopf.quelle} options={QUELLE_OPTIONEN} onChange={(v) => kopfFeldAendern("quelle", v)} disabled={!canEdit} ariaLabel="Quelle" />
                            </FeldMitHilfe>
                        </Col>
                        <Col xs={6} sm={3}>
                            <FeldMitHilfe id="sicherheit" label="Sicherheit" help="Wie belastbar diese Angaben sind.">
                                <SelectList id="sicherheit" value={kopf.sicherheit} options={SICHERHEIT_OPTIONEN} onChange={(v) => kopfFeldAendern("sicherheit", v)} disabled={!canEdit} ariaLabel="Sicherheit" />
                            </FeldMitHilfe>
                        </Col>

                        <Col xs={12}>
                            <FeldMitHilfe id="bemerkung" label="Bemerkung" help="Freitext, etwa der Grund einer Verschiebung.">
                                <textarea id="bemerkung" className="form-control form-control-sm" rows={2} value={kopf.bemerkung || ""} disabled={!canEdit} onChange={(e) => kopfFeldAendern("bemerkung", e.target.value)} />
                            </FeldMitHilfe>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {ladungsEintraege.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <HeaderWithHelp help="Alle Palettenstellplätze dieses LKW, farblich nach Marke. Mit der Maus über eine Palette fahren zeigt Farbton, Menge, Marke und Status.">
                            Lastwagen
                        </HeaderWithHelp>
                    </Card.Header>
                    <Card.Body>
                        <LkwLadung kapazitaetPaletten={kapazitaetPaletten} eintraege={ladungsEintraege} />
                    </Card.Body>
                </Card>
            )}

            <h2 className="h6">
                <HeaderWithHelp help="Alle Farbtöne dieser Lieferung. Direkt in der Tabelle eingeben, Tab springt zur nächsten Zelle, Enter in der letzten Zeile legt eine neue Zeile an.">
                    Positionen {positionenDirty && <span className="badge text-bg-warning ms-2">ungespeichert</span>}
                </HeaderWithHelp>
            </h2>
            <LieferPositionenTabelle
                positionen={positionen}
                canEdit={canEdit}
                onChange={(neu) => {
                    const entfernteJetzt = positionen.filter((alt) => alt.id && !neu.some((n) => n._key === alt._key)).map((alt) => alt.id);
                    if (entfernteJetzt.length > 0) {
                        setEntfernteIds((ids) => [...ids, ...entfernteJetzt]);
                    }
                    setPositionen(neu);
                }}
            />

            {aenderungen.length > 0 && (
                <>
                    <h2 className="h6 mt-4">
                        <HeaderWithHelp help="Wer wann was an dieser Lieferung geändert hat, mit altem und neuem Wert und der Tagesdifferenz bei Terminverschiebungen.">
                            Änderungshistorie
                        </HeaderWithHelp>
                    </h2>
                    <Card>
                        <Card.Body>
                            <AenderungsZeitleiste aenderungen={aenderungen} />
                        </Card.Body>
                    </Card>
                </>
            )}

            <UngespeichertHinweis
                show={zeigeHinweis}
                speichern={speichertGerade}
                onBleiben={() => setZeigeHinweis(false)}
                onVerwerfenUndVerlassen={() => {
                    // Erst den Schutz synchron ausschalten, DANN navigieren -
                    // sonst prüft der "before"-Listener (siehe oben) beim
                    // sofort folgenden router.visit() noch den alten,
                    // veralteten Stand von dirtyRef.current (der über einen
                    // Effekt erst nach dem nächsten Render aktualisiert
                    // würde) und fängt die eigene Navigation gleich wieder ab
                    // (Sichtprüfung 1, Befund 3: "Verwerfen und verlassen"
                    // verließ die Seite bislang nicht).
                    dirtyRef.current = false;
                    setKopfDirty(false);
                    setEntfernteIds([]);
                    setPositionen((ps) => ps.map((p) => ({...p, _dirty: false})));
                    setZeigeHinweis(false);
                    const ziel = zielUrl;
                    setZielUrl(null);
                    if (ziel) {
                        router.visit(ziel);
                    }
                }}
                onSpeichernUndVerlassen={() => {
                    speichern().then(({neueId}) => {
                        setZeigeHinweis(false);
                        const ziel = zielUrl || (istEntwurf ? `/cosmos/lieferungen/${neueId}` : null);
                        setZielUrl(null);
                        if (ziel) {
                            router.visit(ziel, {replace: istEntwurf && !zielUrl});
                        }
                    });
                }}
            />

            <BestaetigenModal
                show={loeschenOffen}
                title="Lieferung löschen"
                confirmLabel="Löschen"
                confirmVariant="danger"
                confirmHelp="Löscht diese Lieferung samt aller Positionen jetzt unwiderruflich - die Änderungshistorie bleibt erhalten."
                cancelHelp="Schließt diesen Hinweis, ohne die Lieferung zu löschen."
                busy={loeschtGerade}
                onCancel={() => setLoeschenOffen(false)}
                onConfirm={loeschen}
            >
                Diese Lieferung mit {positionenText(positionen.length)} wird unwiderruflich gelöscht. Die bereits
                protokollierten Änderungen bleiben in der Änderungshistorie erhalten.
            </BestaetigenModal>
        </Container>
    );
}
